import { useEffect, useRef, useState } from 'react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { useGSAP } from '@gsap/react'
import BaseLabel from '../../../components/general/BaseLabel'
import { TSoftwareFunctions } from '../../../types/index'

gsap.registerPlugin(ScrollTrigger)

type SoftwareFeatureScrollProps = Pick<
    TSoftwareFunctions,
    | 'step1Label' | 'step1Headline'
    | 'step2Label' | 'step2Headline'
    | 'logoText' | 'labelSidebar' | 'bottomTextSidebar'
    | 'coreFunctions' | 'extensions'
>

export default function SoftwareFeatureScroll({
    step1Label,
    step1Headline,
    step2Label,
    step2Headline,
    logoText,
    labelSidebar,
    bottomTextSidebar,
    coreFunctions,
    extensions,
}: SoftwareFeatureScrollProps) {
    const containerRef   = useRef<HTMLDivElement>(null)
    const dashboardRef   = useRef<HTMLDivElement>(null)
    const mainContentRef = useRef<HTMLDivElement>(null)
    const title1Ref      = useRef<HTMLDivElement>(null)
    const title2Ref      = useRef<HTMLDivElement>(null)
    const extTextRef     = useRef<HTMLParagraphElement>(null)
    const extListRef     = useRef<HTMLDivElement>(null)
    const coreRefs       = useRef<(HTMLDivElement | null)[]>([])
    const extRefs        = useRef<(HTMLDivElement | null)[]>([])

    const zoomScale = 1.62
    const zoomX = '26%'
    const zoomY = '-19%'
    const extVisibleCount = Math.min(3, extensions.length)
    const extMinCardHeight = 40
    const extGap = 10
    const [extViewportHeight, setExtViewportHeight] = useState<number | null>(null)

    const measureExtensionLayout = () => {
        const measuredHeights = extensions.map((_, i) => {
            const h = extRefs.current[i]?.getBoundingClientRect().height ?? 0
            return h > 0 ? h : extMinCardHeight
        })

        const viewport = measuredHeights
            .slice(0, extVisibleCount)
            .reduce((sum, h) => sum + h, 0) + Math.max(0, extVisibleCount - 1) * extGap

        return {
            measuredHeights,
            viewport,
        }
    }

    useEffect(() => {
        const updateMeasurements = () => {
            const { viewport } = measureExtensionLayout()
            setExtViewportHeight(viewport)
        }

        updateMeasurements()
        window.addEventListener('resize', updateMeasurements)

        return () => {
            window.removeEventListener('resize', updateMeasurements)
        }
    }, [extensions.length])

    // ── GSAP timeline scrubbed to scroll ──────────────────────────────────────
    // Timeline uses 0-100 "units" that map to scroll progress through the 500vh container.
    // Phases:
    //   0–4   : Title 1 fades in
    //   5–30  : Core widgets reveal one-by-one
    //   36–40 : Title 1 fades out
    //   36–50 : Dashboard zooms into sidebar, main content fades
    //   50–54 : Title 2 fades in
    //   50–82 : Extensions reveal one-by-one
    //   84–88 : "Und viel mehr" text appears
    //   88–100: Hold (everything stays visible while user reads)
    // ─────────────────────────────────────────────────────────────────────────
    useGSAP(() => {
        gsap.set(dashboardRef.current, { transformOrigin: 'center center' })

        const tl = gsap.timeline({
            scrollTrigger: {
                trigger: containerRef.current,
                start: 'top top',
                end: 'bottom bottom',
                scrub: 1.5,
            },
        })

        // Phase 1 — Core widgets (5 → 30)
        const coreStart = 5
        const coreLen   = 25
        const coreDur   = coreLen / Math.max(coreFunctions.length, 1)

        coreFunctions.forEach((_, i) => {
            tl.fromTo(
                coreRefs.current[i],
                { opacity: 0, y: 14 },
                { opacity: 1, y: 0, duration: Math.max(coreDur * 0.65, 2) },
                coreStart + i * coreDur,
            )
        })

        // Phase 2 — Transition into sidebar (36 → 50)
        tl.to(title1Ref.current, { opacity: 0, duration: 4 }, 36)
        tl.to(dashboardRef.current, { scale: zoomScale, x: zoomX, y: zoomY, duration: 13, ease: 'none' }, 36)
        tl.to(mainContentRef.current, { opacity: 0.05, duration: 10 }, 36)

        // Phase 2 — Title 2
        tl.fromTo(title2Ref.current, { opacity: 0, y: 10 }, { opacity: 1, y: 0, duration: 4 }, 50)

        // Phase 3 — Extensions (50 → 82)
        const extStart = 50
        const extLen   = 32
        const extDur   = extLen / Math.max(extensions.length, 1)
        const { measuredHeights } = measureExtensionLayout()

        // Reveal first up to 3 cards.
        extensions.slice(0, 3).forEach((_, i) => {
            tl.fromTo(
                extRefs.current[i],
                { opacity: 0, x: -10 },
                { opacity: 1, x: 0, duration: Math.max(extDur * 0.65, 2) },
                extStart + i * extDur,
            )
        })

        // If there are more than 3 extensions, roll the stack:
        // top card fades out/up, list shifts up, next card enters at bottom.
        if (extensions.length > 3) {
            for (let i = 3; i < extensions.length; i += 1) {
                const at = extStart + i * extDur
                const hiddenCount = i - 2
                const hiddenHeights = measuredHeights
                    .slice(0, hiddenCount)
                    .reduce((sum, h) => sum + h, 0)
                const shiftY = hiddenHeights + hiddenCount * extGap

                tl.to(
                    extRefs.current[i - 3],
                    { opacity: 0, y: -8, duration: Math.max(extDur * 0.65, 2) },
                    at,
                )
                tl.to(
                    extListRef.current,
                    { y: -shiftY, duration: Math.max(extDur * 0.65, 2), ease: 'none' },
                    at,
                )
                tl.fromTo(
                    extRefs.current[i],
                    { opacity: 0, x: -10, y: 8 },
                    { opacity: 1, x: 0, y: 0, duration: Math.max(extDur * 0.65, 2) },
                    at,
                )
            }
        }

        // Phase 3 — Closing text + hold
        tl.fromTo(extTextRef.current, { opacity: 0 }, { opacity: 1, duration: 3 }, 84)
        tl.to({}, { duration: 14 }, 86) // keep scroll pinned while user reads
    }, { scope: containerRef, dependencies: [coreFunctions.length, extensions.length, zoomScale, zoomX, zoomY, extGap, extViewportHeight] })

    // Grid columns: 3-col for ≤3 or ≥5, 2-col for exactly 4
    const coreGridCols =
        coreFunctions.length === 4 ? 'grid-cols-2' : 'grid-cols-3'

    return (
        <section ref={containerRef} className="relative h-[500vh] hidden lg:block pb-32">
            <div className="sticky top-0 h-screen flex items-center justify-center overflow-hidden px-10 bg-base-100">

                {/* ── Animated titles ──────────────────────────────────────────── */}
                <div className="absolute top-20 left-0 w-full text-center z-20 pointer-events-none px-4">
                    <div ref={title1Ref} className="absolute inset-0 flex flex-col items-center" style={{ opacity: 1 }}>
                        <BaseLabel label={step1Label} color="primary" />
                        <h2 className="font-headline font-bold text-3xl lg:text-4xl text-base-content mt-2">
                            {step1Headline}
                        </h2>
                    </div>
                    <div ref={title2Ref} className="absolute inset-0 flex flex-col items-center" style={{ opacity: 0 }}>
                        <BaseLabel label={step2Label} color="secondary" />
                        <h2 className="font-headline font-bold text-3xl lg:text-4xl text-base-content mt-2">
                            {step2Headline}
                        </h2>
                    </div>
                </div>

                {/* ── Dashboard window ─────────────────────────────────────────── */}
                <div
                    ref={dashboardRef}
                    className="w-full max-w-5xl aspect-[16/9] bg-base-100 border border-base-300/60 rounded-2xl flex flex-col overflow-hidden shadow-2xl mt-24 origin-center"
                >
                    {/* Mac-style top bar */}
                    <div className="h-10 bg-base-200 border-b border-base-300/60 flex items-center px-4 gap-2 shrink-0">
                        <div className="w-2.5 h-2.5 rounded-full bg-base-content/20" />
                        <div className="w-2.5 h-2.5 rounded-full bg-base-content/20" />
                        <div className="w-2.5 h-2.5 rounded-full bg-base-content/20" />
                    </div>

                    {/* Body */}
                    <div className="flex-1 flex overflow-hidden min-h-0">

                        {/* ── Sidebar ─────────────────────────────────────────────── */}
                        <div className="w-[30%] bg-base-200 border-r border-base-300/60 p-5 flex flex-col gap-2 relative z-10 overflow-y-auto">
                            {/* Logo */}
                            <div className="flex items-center gap-3 mb-6 shrink-0">
                                <div className="w-6 h-6 rounded bg-primary/20 flex items-center justify-center shrink-0">
                                    <div className="w-2.5 h-2.5 bg-primary rounded-sm" />
                                </div>
                                <span className="font-headline font-bold text-base-content/70 text-sm truncate">
                                    {logoText}
                                </span>
                            </div>

                            {/* Nav skeleton */}
                            <div className="space-y-2 mb-6 shrink-0">
                                <div className="h-8 bg-base-content/10 rounded w-full" />
                                <div className="h-8 bg-base-content/5 rounded w-5/6" />
                                <div className="h-8 bg-base-content/5 rounded w-4/6" />
                                <div className="h-8 bg-base-content/5 rounded w-full" />
                            </div>

                            {/* Extensions — animate in during Phase 3 */}
                            <div className="flex flex-col gap-2.5 mt-auto">
                                <p className="text-[10px] font-bold text-secondary uppercase tracking-widest mb-1 shrink-0">
                                    {labelSidebar}
                                </p>

                                <div className="overflow-hidden" style={{ height: extViewportHeight ? `${extViewportHeight}px` : undefined }}>
                                    <div ref={extListRef} className="flex flex-col gap-2.5">
                                        {extensions.map((ext, i) => (
                                            <div
                                                key={i}
                                                ref={(el) => { extRefs.current[i] = el }}
                                                style={{ opacity: 0 }}
                                                className="min-h-[40px] bg-secondary/10 border border-secondary/30 rounded-md flex items-start px-3 py-2 gap-2.5"
                                            >
                                                <div className="w-2 h-2 rounded-full bg-secondary shrink-0 mt-1" />
                                                <div>
                                                    <p className="text-secondary text-[11px] font-bold leading-tight">
                                                        {ext.title}
                                                    </p>
                                                    <p className="text-base-content/40 text-[9px] mt-0.5 leading-tight">
                                                        {ext.description}
                                                    </p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <p
                                    ref={extTextRef}
                                    style={{ opacity: 0 }}
                                    className="text-base-content/40 text-[10px] italic leading-relaxed mt-1 pl-1"
                                >
                                    {bottomTextSidebar}
                                </p>
                            </div>
                        </div>

                        {/* ── Main content area ────────────────────────────────────── */}
                        <div
                            ref={mainContentRef}
                            className="w-[70%] p-6 bg-base-100 flex flex-col gap-4 overflow-hidden"
                        >
                            {/* Header row */}
                            <div className="flex justify-between items-center shrink-0">
                                <div className="h-7 w-1/3 bg-base-content/10 rounded" />
                                <div className="h-7 w-16 bg-primary/20 border border-primary/30 rounded" />
                            </div>

                            {/* Core function widgets — animate in during Phase 1 */}
                            <div className={`grid ${coreGridCols} gap-4`}>
                                {coreFunctions.map((core, i) => (
                                    <div
                                        key={i}
                                        ref={(el) => { coreRefs.current[i] = el }}
                                        style={{ opacity: 0 }}
                                        className="bg-base-200 border border-base-300/60 rounded-xl p-4 flex flex-col gap-2 min-h-[90px]"
                                    >
                                        <span className="text-[13px] text-base-content/90 uppercase tracking-wider font-bold">
                                            {core.title}
                                        </span>
                                        <p className="text-base-content/60 !text-[12px] pb-1 leading-tight">
                                            {core.description}
                                        </p>
                                        <div className="h-8 w-full bg-base-content/5 rounded-md mt-auto" />
                                    </div>
                                ))}
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </section>
    )
}
